// Agent Console — admin UI for the strictly READ-ONLY data-analyst agent.
//
// A conversation list (left) + a streaming transcript (right). Each turn streams
// SSE frames from POST /api/admin/agent-console/conversations/{id}/message: the
// assistant's markdown answer, its extended thinking, and a live tool-call trace
// (each call shows the permission decision — this agent can only read). On
// completion we reload the canonical persisted transcript (the audit trail).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useGuruWidgetViewContext } from '../hooks/useGuruWidgetViewContext';
import { useRetriedLoad } from '../hooks/useRetriedLoad';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { StatusBadge } from '../components/common/StatusBadge';
import { ToolCallTrace } from '../components/agentConsole/ToolCallTrace';
import { ProfileAccessModal } from '../components/agentConsole/ProfileAccessModal';
import { ProfileInspectorPanel } from '../components/agentConsole/ProfileInspectorPanel';
import { ConsoleSettingsPanel } from '../components/agentConsole/ConsoleSettingsPanel';
import ModelSelect from '../components/agentConsole/ModelSelect';
import { BannerLoadRetry } from '../components/agentConsole/BannerLoadRetry';
import { MessageTimestamp } from '../components/agentConsole/MessageTimestamp';
import FeedbackControls from '../components/agentConsole/FeedbackControls';
import {
  addUsageToTotals,
  appendLiveText,
  appendLiveThinking,
  applyLiveToolResult,
  buildTranscriptTimeline,
  cancelConsoleMessage,
  injectConsoleMessage,
  canStartTurn,
  composerPlaceholder,
  shouldAutoScrollToBottom,
  createConversation,
  describeTotalsTooltip,
  renameConversation,
  EMPTY_TOTALS,
  extractCurrentPhase,
  formatCostUsd,
  formatTokens,
  getConsoleModels,
  getConversationDetail,
  getProfiles,
  listConversations,
  listConversationOwners,
  splitFinalAnswer,
  reattachConsoleMessage,
  streamConsoleMessage,
  toolCallViewFromUse,
  uploadConversationAttachment,
  type AssistantBlock,
  type ConsoleEvent,
  type ConsoleProfile,
  type ModelOption,
  type ConversationDetail,
  type FeedbackContext,
  type ConversationOwner,
  type ConversationRead,
  type ConversationState,
  type ConversationTotals,
  type CurrentPhase,
  type UsageEvent,
} from '../services/agentConsole';
import { AgentFileCard } from '../components/agentConsole/AgentFileCard';
import {
  ConversationDecisionCard,
  SentMessageCard,
} from '../components/agentConsole/ConversationMessageCard';
import {
  liftAgentFileCards,
  mergeAgentFiles,
  type AgentFileData,
} from '../components/agentConsole/agentFileCards';
import { AttachmentTray, DropVeil } from '../components/chat/AttachmentTray';
import { MessageAttachmentChips } from '../components/chat/MessageAttachmentChips';
import { effectiveMimeType } from '../components/chat/composerAttachments';
import {
  attachmentsByMessageId,
  type MessageAttachment,
} from '../components/chat/messageAttachments';
import { useComposerAttachments } from '../components/chat/useComposerAttachments';
import styles from './AgentConsolePage.module.css';

// This console's API prefix (matches the service helpers above).
const CONSOLE_BASE = '/admin/agent-console';

const STATE_BADGE: Record<
  ConversationState,
  { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }
> = {
  created: { variant: 'neutral', label: 'new' },
  running: { variant: 'info', label: 'running' },
  awaiting_input: { variant: 'success', label: 'ready' },
  failed: { variant: 'error', label: 'failed' },
  cancelled: { variant: 'warning', label: 'cancelled' },
  archived: { variant: 'neutral', label: 'archived' },
};

function relativeDate(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

// Owner attribution helpers — a small initials avatar with a stable per-person
// color so the shared history list reads at a glance.
const AVATAR_COLORS = [
  '#0B4FA3',
  '#10B981',
  '#F59E0B',
  '#8957E5',
  '#EE3C33',
  '#1A6BC4',
  '#0D9488',
];

function ownerLabel(convo: ConversationRead): string {
  return convo.owner_name || convo.owner_email || 'Unknown user';
}

function ownerInitials(convo: ConversationRead): string {
  const label = ownerLabel(convo).replace(/\(.*?\)/g, '').trim();
  const parts = label.split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]);
  return (letters.join('') || '?').toUpperCase();
}

function avatarColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i += 1) {
    hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function OwnerAvatar({ convo }: { convo: ConversationRead }) {
  return (
    <span
      className={styles.ownerAvatar}
      style={{ backgroundColor: avatarColor(convo.user_id) }}
      title={ownerLabel(convo)}
      aria-hidden="true"
    >
      {ownerInitials(convo)}
    </span>
  );
}

function AssistantMarkdown({ content }: { content: string }) {
  return (
    <div className={styles.markdownContent}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
}

/**
 * Render a sequence of assistant blocks in the order the model emitted them.
 * Used both for the "final answer" body and (when toggled open) for the full
 * trace of intermediate work.
 */
function AssistantBlocks({ blocks }: { blocks: AssistantBlock[] }) {
  return (
    <>
      {blocks.map((block, idx) => {
        if (block.kind === 'text') {
          return <AssistantMarkdown key={`b-${idx}`} content={block.text} />;
        }
        if (block.kind === 'thinking') {
          return (
            <div key={`b-${idx}`} className={styles.traceThinking}>
              {block.text}
            </div>
          );
        }
        return (
          <div key={`b-${idx}`} className={styles.toolRow}>
            <ToolCallTrace tool={block.tool} />
          </div>
        );
      })}
    </>
  );
}

/**
 * The "status replace" view shown while the agent is still iterating. Only
 * the latest narration (text or thinking) and the tool calls under it are
 * visible; each new narration that arrives replaces the previous phase, so
 * the bubble doesn't accumulate into a wall.
 */
function CurrentPhasePanel({ phase }: { phase: CurrentPhase }) {
  if (!phase.narration && phase.tools.length === 0) return null;
  return (
    <div className={styles.currentPhase}>
      {phase.narration && phase.narration.kind === 'text' && (
        <p className={styles.phaseNarration}>{phase.narration.text}</p>
      )}
      {phase.narration && phase.narration.kind === 'thinking' && (
        <p className={`${styles.phaseNarration} ${styles.phaseThinking}`}>
          <em>{phase.narration.text}</em>
        </p>
      )}
      {phase.tools.map((tool, i) => (
        <div key={`phase-tool-${i}`} className={styles.toolRow}>
          <ToolCallTrace tool={tool} />
        </div>
      ))}
    </div>
  );
}

/**
 * One assistant turn. Default: just the final answer. While the model is still
 * iterating (final answer not yet streamed), show the live status phase only.
 * A "View thinking trace" button surfaces the intermediate narration + tool
 * sequence on demand.
 */
function AssistantTurn({
  blocks,
  isStreaming,
  feedback,
}: {
  blocks: AssistantBlock[];
  isStreaming: boolean;
  // When supplied (persisted, owner-viewed turns), render the shared 👍/👎 +
  // comment control under the answer. Omitted for the live in-flight turn.
  feedback?: FeedbackContext | null;
}) {
  const [traceOpen, setTraceOpen] = useState(false);
  const { intermediate, finalAnswer } = splitFinalAnswer(blocks);

  // Still working: show the rolling status phase only.
  if (isStreaming && finalAnswer.length === 0) {
    return <CurrentPhasePanel phase={extractCurrentPhase(intermediate)} />;
  }

  return (
    <>
      <AssistantBlocks blocks={finalAnswer} />
      {intermediate.length > 0 && (
        <button
          type="button"
          className={styles.traceToggle}
          onClick={() => setTraceOpen((v) => !v)}
        >
          {traceOpen ? 'Hide thinking trace' : 'View thinking trace'}
        </button>
      )}
      {traceOpen && intermediate.length > 0 && (
        <div className={styles.traceContainer}>
          <AssistantBlocks blocks={intermediate} />
        </div>
      )}
      {feedback && !isStreaming && finalAnswer.length > 0 && (
        <FeedbackControls context={feedback} />
      )}
    </>
  );
}

function UsageMeta({ usage }: { usage: UsageEvent }) {
  const parts: string[] = [];
  const tokens = formatTokens(usage);
  if (tokens) parts.push(tokens);
  if (usage.cost_usd != null) parts.push(formatCostUsd(usage.cost_usd));
  if (usage.stop_reason) parts.push(usage.stop_reason);
  if (parts.length === 0) return null;
  return <div className={styles.meta}>{parts.join(' · ')}</div>;
}

function ConversationTotalsBadge({
  persisted,
  liveUsage,
}: {
  persisted: ConversationTotals;
  liveUsage: UsageEvent | null;
}) {
  // Fold the in-flight turn into the displayed total so the meter keeps moving
  // mid-stream. The canonical totals come back from the server when the turn
  // finishes (which is when we reload the detail), so this is just for the
  // few seconds between "result" frame and "done".
  const totals = liveUsage ? addUsageToTotals(persisted, liveUsage) : persisted;
  if (totals.turn_count === 0) return null;
  const tokens = formatTokens(totals) ?? '0 tokens';
  return (
    <span className={styles.totalsBadge} title={describeTotalsTooltip(totals)}>
      Σ {tokens} · {formatCostUsd(totals.cost_usd)}
    </span>
  );
}

export function AgentConsolePage() {
  const [conversations, setConversations] = useState<ConversationRead[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [streaming, setStreaming] = useState(false);
  // Files staged for the next message: drag-drop / paste / picker (shared hook).
  // Mirrored in a ref so the async send closure reads the latest staged list.
  const attach = useComposerAttachments({ disabled: streaming });
  const stagedRef = useRef<File[]>([]);
  stagedRef.current = attach.files;
  // True between a Stop click and the server's terminal cancelled/done frames.
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // History browsing (Track B): "mine" vs everyone's, narrowed by who started
  // it and a title search. Sharing copies a deep-link to the active thread.
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [ownerFilter, setOwnerFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [owners, setOwners] = useState<ConversationOwner[]>([]);
  const [copiedHint, setCopiedHint] = useState(false);

  // Inline rename of a sidebar thread (the universal editable-title affordance).
  // Only offered in the "mine" scope, where every listed thread is the viewer's
  // own — the backend rename is owner-only, so we never show a control that 404s.
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState('');

  // The three console modals (overlay, so no layout reflow / width fighting):
  // the plain-language "Can access" summary, the developer Inspect view, and
  // the runtime Settings editor.
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Console profiles (loaded once). The selected profile drives which agent
  // configuration each new turn runs under, and its plain-language data-access
  // groups travel on the profile itself.
  const [profiles, setProfiles] = useState<ConsoleProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  // The server's default profile id (Analyst). Used as the fallback when a
  // conversation has no persisted profile (legacy rows) on restore.
  const [defaultProfileId, setDefaultProfileId] = useState<string>('');

  // Selectable Anthropic models (loaded once). The picker preselects the active
  // conversation's model on reload, the chosen profile's default for a fresh
  // thread, and the global default before either is known.
  const [models, setModels] = useState<ModelOption[]>([]);
  const [globalDefaultModel, setGlobalDefaultModel] = useState<string>('');
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  // The strongest selectable model — drives the "try a stronger model" 👎 nudge.
  const [strongestModel, setStrongestModel] = useState<string>('');

  // Live (in-flight) turn scratch state. The model emits text / thinking /
  // tool_use blocks interleaved; ``liveBlocks`` is a single ordered list of
  // those (same shape used to render the persisted view), so what the user
  // sees mid-stream matches what gets written to the audit trail.
  const [liveUserPrompt, setLiveUserPrompt] = useState<string | null>(null);
  // Chips for the live user bubble — the files that were just uploaded with the
  // in-flight turn (the persisted view re-derives them from detail.attachments).
  const [liveUserAttachments, setLiveUserAttachments] = useState<
    MessageAttachment[] | null
  >(null);
  const [liveBlocks, setLiveBlocks] = useState<AssistantBlock[]>([]);
  // Files the agent attached during the in-flight turn (attach_file) — lifted
  // from successful ack frames the moment they land; the persisted view
  // re-derives them from the saved trace via buildTranscriptTimeline.
  const [liveAgentFiles, setLiveAgentFiles] = useState<AgentFileData[]>([]);
  const [liveUsage, setLiveUsage] = useState<UsageEvent | null>(null);
  const [liveState, setLiveState] = useState<ConversationState | null>(null);
  // The conversation the in-flight turn belongs to. The live scratch state above
  // is page-global (one turn at a time), so we tag it with its thread and only
  // render/attribute it there — otherwise switching conversations mid-turn bleeds
  // the live "thinking" bubble onto the thread you switched to.
  const [liveConvId, setLiveConvId] = useState<string | null>(null);

  const transcriptRef = useRef<HTMLDivElement>(null);
  // Tracks the transcript's scrollHeight *before* the latest render, so the
  // auto-scroll gate can ask "was the user at the bottom before this chunk?"
  // instead of "are they at the bottom after it?" (a fat SSE frame would
  // always look like the user had scrolled up otherwise).
  const prevScrollHeightRef = useRef(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Set by "+ New analysis": on the welcome screen the composer textarea is not
  // mounted yet (it lives in the active-conversation branch), so a synchronous
  // ``inputRef.current?.focus()`` right after ``setActiveId`` is a silent no-op.
  // The flag survives until the commit that mounts the composer, where the
  // effect below consumes it.
  const focusComposerOnOpenRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  // The id of the in-flight turn (from its ``started`` frame) so Stop / inject
  // target exactly this run, never a later one.
  const runIdRef = useRef<string | null>(null);
  // The conversation id whose profile we last synced into the selector. Guards
  // the restore effect so it runs once per conversation *switch* and never
  // re-syncs (clobbering a deliberate dropdown change) when the same
  // conversation's detail merely refreshes after a turn.
  const lastSyncedProfileConvoRef = useRef<string | null>(null);

  const activeConversation: ConversationRead | null =
    detail?.conversation && detail.conversation.id === activeId
      ? detail.conversation
      : conversations.find((c) => c.id === activeId) ?? null;

  // Latest activeId, readable inside async closures (e.g. a turn's post-run
  // reload) without stale-capturing it — so a turn that finishes on a thread
  // you've since navigated away from doesn't clobber the one you're now reading.
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeId;

  // Only attribute the in-flight turn (and its 'running' state) to the thread it
  // actually runs in — the live scratch state is page-global.
  const liveIsActive = liveConvId != null && liveConvId === activeId;
  const streamingHere = streaming && liveIsActive;

  const effectiveState: ConversationState | null =
    (liveIsActive ? liveState : null) ?? activeConversation?.state ?? null;
  // When viewing a shared (someone else's) conversation, the transcript is
  // read-only — only the starter can continue a turn. We trust the detail's
  // flag once it's loaded for the active thread; otherwise assume ownership
  // (a freshly-created thread is always yours).
  const viewerIsOwner =
    detail && detail.conversation.id === activeId ? detail.viewer_is_owner : true;
  const canSend =
    !!activeConversation &&
    !streaming &&
    viewerIsOwner &&
    // A retired-agent thread is refused server-side, so never offer the Send.
    !activeConversation.profile_retired &&
    canStartTurn(effectiveState ?? 'created');

  const selectedProfile: ConsoleProfile | null =
    profiles.find((p) => p.id === selectedProfileId) ?? null;

  // Tell the Guru feedback widget which AI conversation is on screen.
  useGuruWidgetViewContext('agent_console_chat', {
    copilot: 'agent-console',
    conversation_id: activeId ?? undefined,
    conversation_title: activeConversation?.title ?? undefined,
    agent_name: selectedProfile?.name,
    model_id: selectedModelId || undefined,
  });

  // The active thread's server-resolved profile when it is LOCKED to a
  // non-picker (door/headless) profile — e.g. a Tech Support Co-Pilot per-email
  // thread surfacing in the shared list. Rendered as a fixed label instead of
  // the selector: those threads always run their own agent, and rendering the
  // select with an out-of-list value would make the browser display the FIRST
  // option ("Analyst") while silently swallowing clicks on it.
  const lockedProfileName: string | null =
    activeConversation && activeConversation.profile_locked
      ? activeConversation.profile_name || activeConversation.profile_id || null
      : null;

  // A thread whose agent was retired: readable, but it can never take another
  // turn (the server refuses rather than substituting the default Analyst). The
  // composer is disabled and the caption says so, instead of promising the
  // thread "always runs" an agent that never will again.
  const profileRetired = Boolean(activeConversation?.profile_retired);

  // Load the console profiles + master access catalog, and the selectable
  // models. Each is a bounded-retry load (kanban 2784d245): a single transient
  // miss right after login (cold API, 5xx, refresh race) used to be swallowed by
  // a load-once effect, leaving the picker empty for the page's whole lifetime
  // while turns silently ran the default profile. Now a miss is retried, and a
  // total failure renders an in-place Retry control instead of the fallback.
  const profilesLoad = useRetriedLoad(getProfiles);
  const modelsLoad = useRetriedLoad(getConsoleModels);

  useEffect(() => {
    const data = profilesLoad.data;
    if (!data) return;
    setProfiles(data.profiles);
    setDefaultProfileId(data.default_profile_id);
    setSelectedProfileId((prev) => prev || data.default_profile_id);
  }, [profilesLoad.data]);

  useEffect(() => {
    const data = modelsLoad.data;
    if (!data) return;
    setModels(data.models);
    setGlobalDefaultModel(data.default_model);
    setStrongestModel(data.strongest_model);
    setSelectedModelId((prev) => prev || data.default_model);
  }, [modelsLoad.data]);

  const loadConversations = useCallback(async () => {
    try {
      const items = await listConversations({
        includeArchived: false,
        limit: 100,
        scope,
        createdBy: scope === 'all' && ownerFilter ? ownerFilter : undefined,
        q: search.trim() || undefined,
      });
      setConversations(items);
      setActiveId((prev) => prev ?? items[0]?.id ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversations');
    }
  }, [scope, ownerFilter, search]);

  // Persist a renamed thread title (optimistic): patch the list in place, then
  // call the shared rename endpoint; on failure, re-fetch to resync.
  const commitTitleRename = useCallback(
    async (id: string, raw: string) => {
      const title = raw.trim();
      setEditingTitleId(null);
      setTitleDraft('');
      if (!title) return;
      const current = conversations.find((c) => c.id === id);
      if (current && current.title === title) return;
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, title } : c)),
      );
      try {
        await renameConversation(id, title);
      } catch {
        void loadConversations();
      }
    },
    [conversations, loadConversations],
  );

  const loadOwners = useCallback(async () => {
    try {
      setOwners(await listConversationOwners());
    } catch {
      /* non-fatal: the "started by" filter just stays empty */
    }
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    const data = await getConversationDetail(id);
    setDetail(data);
  }, []);

  // Deep-link support: open a shared conversation from ?conversation=<id>.
  useEffect(() => {
    const cid = new URLSearchParams(window.location.search).get('conversation');
    if (cid) setActiveId(cid);
  }, []);

  // (Re)load the list whenever scope / starter / search changes. Debounced so
  // typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      loadConversations();
    }, 200);
    return () => clearTimeout(t);
  }, [loadConversations]);

  useEffect(() => {
    loadOwners();
  }, [loadOwners]);

  // Load the transcript whenever the active conversation changes (unless we
  // already hold its freshly-created detail).
  useEffect(() => {
    if (!activeId) {
      setDetail(null);
      return;
    }
    if (detail?.conversation.id === activeId) return;
    loadDetail(activeId).catch((err) =>
      setError(err instanceof Error ? err.message : 'Failed to load conversation'),
    );
  }, [activeId, detail, loadDetail]);

  // Restore the profile selector from the conversation you're viewing, so opening
  // a saved thread (sidebar click, ?conversation= deep-link, or the first/most-
  // recent auto-load) switches the agent back to the profile it runs under. Sync
  // only when the active conversation *changes* (keyed off a ref) so a deliberate
  // mid-thread profile change isn't clobbered when the detail reloads after a
  // turn. Legacy threads with no persisted profile fall back to the server
  // default.
  useEffect(() => {
    if (!activeId) {
      lastSyncedProfileConvoRef.current = null;
      return;
    }
    if (lastSyncedProfileConvoRef.current === activeId) return;
    // Wait until the row for this id is actually available — a deep-linked thread
    // isn't in the list until its detail loads.
    if (!activeConversation || activeConversation.id !== activeId) return;
    lastSyncedProfileConvoRef.current = activeId;
    // A LOCKED (door/headless) thread never occupies the selector state: its
    // agent is fixed server-side and rendered as a label instead of the select.
    // Adopting its id here would silently leak the door profile into every
    // subsequent send and "+ New analysis" while the select — whose options are
    // picker profiles only — kept DISPLAYING "Analyst" (the browser falls back
    // to the first option for an unmatched value, which also swallows an
    // explicit re-click of that option: no change event ever fires).
    setSelectedProfileId(
      activeConversation.profile_locked
        ? defaultProfileId
        : activeConversation.profile_id || defaultProfileId,
    );
    // Restore the model picker from the thread too (it persists like the profile).
    setSelectedModelId(activeConversation.model);
  }, [activeId, activeConversation, defaultProfileId]);

  // Keep the transcript pinned to the latest output while it streams — but
  // only if the user was *already* at the bottom BEFORE this content update.
  // Measuring against the post-mutation scrollHeight broke for fat SSE frames:
  // a single chunk could grow the transcript by hundreds of pixels, the gate
  // would see a big distance from the new bottom and (wrongly) bail, the
  // transcript fell behind by a screen or more, and from then on never caught
  // up. ``prevScrollHeightRef`` carries the pre-mutation height so the gate
  // asks the right question. Instant scrolls (``behavior: 'auto'``) land in
  // one paint, so the next frame's gate check sees an accurate at-bottom
  // state instead of catching us mid-animation.
  useEffect(() => {
    const el = transcriptRef.current;
    if (!el) return;
    const previousScrollHeight = prevScrollHeightRef.current;
    prevScrollHeightRef.current = el.scrollHeight;
    if (
      !shouldAutoScrollToBottom({
        scrollHeight: el.scrollHeight,
        scrollTop: el.scrollTop,
        clientHeight: el.clientHeight,
        previousScrollHeight,
      })
    ) {
      return;
    }
    el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
  }, [detail, liveBlocks, streaming]);

  // Land the cursor in the composer once a freshly-created thread has actually
  // mounted it (see ``focusComposerOnOpenRef``). Keyed on the active id so it
  // runs on the same commit that swaps the welcome panel for the composer.
  useEffect(() => {
    if (!focusComposerOnOpenRef.current) return;
    const el = inputRef.current;
    if (!el) return;
    focusComposerOnOpenRef.current = false;
    el.focus();
  }, [activeId]);

  const handleNewConversation = useCallback(async () => {
    setCreating(true);
    setError(null);
    try {
      // Stamp the currently-selected profile on the new thread so it persists and
      // restores on reload (the backend resolves an empty id to the default).
      // Only ever a PICKER profile: a door/headless id that somehow reached the
      // selector state must not birth a console thread running a different
      // agent than its label (the backend enforces the same rule).
      const convo = await createConversation({
        profile_id: profiles.some((p) => p.id === selectedProfileId)
          ? selectedProfileId
          : undefined,
        model: selectedModelId || undefined,
      });
      setConversations((prev) => [convo, ...prev.filter((c) => c.id !== convo.id)]);
      setDetail({
        conversation: convo,
        messages: [],
        tool_calls: [],
        totals: EMPTY_TOTALS,
        viewer_is_owner: true,
      });
      focusComposerOnOpenRef.current = true;
      setActiveId(convo.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start a new analysis');
    } finally {
      setCreating(false);
    }
  }, [profiles, selectedProfileId, selectedModelId]);

  const resetLive = useCallback(() => {
    setLiveUserPrompt(null);
    setLiveUserAttachments(null);
    setLiveBlocks([]);
    setLiveAgentFiles([]);
    setLiveUsage(null);
    setLiveState(null);
    setLiveConvId(null);
  }, []);

  // Abort the in-flight stream if the console unmounts (SPA navigation away) so a
  // detached fetch can't keep mutating unmounted state and log an error. (Today
  // the server also tears the turn down on disconnect; Phase 2 makes it
  // survivable + reattachable.)
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  // Shared consumer for a live turn stream — a fresh POST (sendPrompt) or a
  // reattach GET (reattachTurn). Feeds SSE frames into the live-block state
  // machine; ``open`` supplies the transport and ``userPrompt`` seeds the live
  // user bubble (null on a reattach — that message is already in the persisted
  // transcript). The turn now runs detached server-side, so a disconnect no longer
  // ends it; this consumer just observes.
  const consumeLiveStream = useCallback(
    async (
      conversationId: string,
      open: (
        onEvent: (evt: ConsoleEvent) => void,
        signal: AbortSignal,
      ) => Promise<void>,
      userPrompt: string | null,
    ) => {
      setError(null);
      setCancelling(false);
      setStreaming(true);
      setLiveUserPrompt(userPrompt);
      setLiveBlocks([]);
      setLiveAgentFiles([]);
      setLiveUsage(null);
      setLiveState('running');
      setLiveConvId(conversationId);

      const controller = new AbortController();
      abortRef.current = controller;
      runIdRef.current = null;
      let liveToolSeq = 0;

      try {
        await open((evt) => {
          switch (evt.type) {
            case 'started':
              // Remember this turn's id so Stop / steer target exactly this run —
              // works on a REATTACHED turn too (the id arrives in the replay).
              runIdRef.current = evt.run_id;
              setLiveState('running');
              break;
            case 'text':
              setLiveBlocks((bs) => appendLiveText(bs, evt.text));
              break;
            case 'thinking':
              setLiveBlocks((bs) => appendLiveThinking(bs, evt.text));
              break;
            case 'tool_use':
              setLiveBlocks((bs) => [
                ...bs,
                {
                  kind: 'tool',
                  tool: toolCallViewFromUse(evt, `live-${liveToolSeq++}`),
                },
              ]);
              break;
            case 'tool_result':
              setLiveBlocks((bs) => {
                const applied = applyLiveToolResult(
                  bs,
                  evt.tool_use_id,
                  evt.is_error,
                  evt.summary,
                );
                // A successful attach_file frame becomes a file card the moment
                // its ack lands (pending/errored chips stay in the trace).
                const { blocks: kept, files } = liftAgentFileCards(applied);
                if (files.length) {
                  setLiveAgentFiles((prev) => mergeAgentFiles(prev, files));
                }
                return kept;
              });
              break;
            case 'result':
              setLiveUsage(evt);
              break;
            case 'state':
              setLiveState(evt.state as ConversationState);
              if (evt.error_message) setError(evt.error_message);
              break;
            case 'title':
              // Auto-naming landed mid-stream — reflect it live in the sidebar and
              // header (the post-turn reload persists it anyway).
              setConversations((prev) =>
                prev.map((c) =>
                  c.id === conversationId ? { ...c, title: evt.title } : c,
                ),
              );
              setDetail((prev) =>
                prev && prev.conversation.id === conversationId
                  ? {
                      ...prev,
                      conversation: { ...prev.conversation, title: evt.title },
                    }
                  : prev,
              );
              break;
            case 'injected':
              // Steered mid-turn: the pre-inject assistant message + this injected
              // user message are now persisted. Reload the canonical transcript so
              // they render in order, and start a fresh live bubble for the
              // steered continuation.
              setLiveUserPrompt(null);
              setLiveBlocks([]);
              // Pre-inject attach cards are persisted with that message — the
              // reload re-derives them, so the live copies reset with the bubble.
              setLiveAgentFiles([]);
              setLiveUsage(null);
              void loadDetail(conversationId).catch(() => undefined);
              break;
            case 'cancelled':
              // A user Stop — render a calm "stopped", not a red error.
              setLiveState('cancelled');
              break;
            case 'error':
              setError(evt.message);
              break;
            case 'done':
              break;
          }
        }, controller.signal);
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(
            err instanceof Error ? err.message : 'The turn failed to stream.',
          );
        }
      } finally {
        setStreaming(false);
        setCancelling(false);
        abortRef.current = null;
        runIdRef.current = null;
        try {
          // Only swap the on-screen transcript to the canonical audit if the user
          // is still viewing THIS thread — a turn that finishes on a thread you've
          // since navigated away from must not clobber the one you're now reading.
          // The list refresh (for the state badges) is always safe.
          const data = await getConversationDetail(conversationId);
          if (activeIdRef.current === conversationId) setDetail(data);
          await loadConversations();
        } catch {
          /* keep the live view if the canonical reload fails */
        }
        resetLive();
      }
    },
    [loadDetail, loadConversations, resetLive],
  );

  const sendPrompt = useCallback(
    async (prompt: string) => {
      if (!prompt || !activeConversation) return;
      const conversationId = activeConversation.id;
      // Snapshot + clear the files staged for this turn, then upload them BEFORE
      // the message so the backend materializes them into the turn's working dir
      // for the agent's Read tool. One failed upload doesn't block the turn.
      const staged = stagedRef.current;
      if (staged.length > 0) {
        attach.clear();
        setLiveUserAttachments(
          staged.map((f, i) => ({
            id: `local-${i}-${f.name}`,
            filename: f.name,
            mimeType: effectiveMimeType(f),
            sizeBytes: f.size,
          })),
        );
        for (const file of staged) {
          try {
            await uploadConversationAttachment(CONSOLE_BASE, conversationId, file);
          } catch {
            /* non-fatal — the turn still runs */
          }
        }
      }
      // A LOCKED (door/headless) thread always runs its own agent — send no profile
      // so the server's lock stays the single source of truth.
      const profileId = activeConversation.profile_locked
        ? undefined
        : selectedProfileId || undefined;
      const model = selectedModelId || undefined;
      await consumeLiveStream(
        conversationId,
        (onEvent, signal) =>
          streamConsoleMessage(
            conversationId,
            prompt,
            onEvent,
            signal,
            profileId,
            model,
          ),
        prompt,
      );
    },
    [activeConversation, selectedProfileId, selectedModelId, consumeLiveStream, attach],
  );

  // Reattach to an already-running turn (no new user message) — the reload /
  // switch-back path. The turn survives the original connection, so this replays
  // what's happened and tails live to completion.
  const reattachTurn = useCallback(
    async (conversationId: string) => {
      await consumeLiveStream(
        conversationId,
        (onEvent, signal) =>
          reattachConsoleMessage(conversationId, onEvent, signal),
        null,
      );
    },
    [consumeLiveStream],
  );

  // When a conversation loads/reloads with server state 'running', reconnect to
  // its in-flight turn so a refresh or switch-back shows Merlin still working,
  // live. Idempotent: only one live stream at a time (single-turn model), and we
  // never re-attach a thread we're already streaming.
  useEffect(() => {
    if (!activeConversation || activeConversation.id !== activeId) return;
    if (activeConversation.state !== 'running') return;
    if (streaming) return;
    if (liveConvId === activeConversation.id) return;
    void reattachTurn(activeConversation.id);
  }, [activeConversation, activeId, streaming, liveConvId, reattachTurn]);

  const handleSend = useCallback(() => {
    // A screenshot pasted with no words still sends — the backend hands the
    // file(s) to the agent (materialized for its Read tool) either way.
    const prompt =
      inputValue.trim() ||
      (stagedRef.current.length > 0 ? '(see attached file(s))' : '');
    if (!prompt || !canSend) return;
    setInputValue('');
    void sendPrompt(prompt);
  }, [inputValue, canSend, sendPrompt]);

  // Hard-stop the in-flight turn (cancel). The server emits the terminal
  // cancelled frames on the existing stream, which settle the UI.
  const handleStop = useCallback(async () => {
    if (!activeConversation || !streaming || cancelling) return;
    setCancelling(true);
    try {
      await cancelConsoleMessage(activeConversation.id, runIdRef.current);
    } catch {
      /* the turn may have just finished — the stream's frames settle the UI */
    }
  }, [activeConversation, streaming, cancelling]);

  // Steer the in-flight turn with the typed text (SDK-native inject): the agent
  // is interrupted and re-queried on the SAME live session, keeping its in-turn
  // context. The injected message + steered continuation arrive on the existing
  // SSE stream — no cancel, no restart.
  const handleInject = useCallback(async () => {
    if (!activeConversation || !streaming) return;
    const text = inputValue.trim();
    if (!text) return;
    setInputValue('');
    try {
      await injectConsoleMessage(activeConversation.id, text, runIdRef.current);
    } catch {
      /* if the turn just ended, a normal Send still works next */
    }
  }, [activeConversation, streaming, inputValue]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    if (streamingHere) {
      // Enter while THIS thread's agent runs steers it with the typed context.
      if (inputValue.trim()) void handleInject();
    } else {
      handleSend();
    }
  };

  const handleShare = useCallback(async () => {
    if (!activeId) return;
    const url = `${window.location.origin}${window.location.pathname}?conversation=${activeId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* clipboard blocked (insecure context) — still flash the hint */
    }
    setCopiedHint(true);
    window.setTimeout(() => setCopiedHint(false), 1800);
  }, [activeId]);

  const timeline = detail
    ? buildTranscriptTimeline(detail.messages, detail.tool_calls)
    : [];
  // Files claimed onto each persisted user message (post-turn) → bubble chips.
  const attachmentsByMsg = attachmentsByMessageId(detail?.attachments);
  const showEmptyTranscript =
    !!detail &&
    timeline.length === 0 &&
    !streamingHere &&
    !(liveIsActive && liveUserPrompt);

  return (
    <div className={styles.container}>
      {/* Sidebar: conversation list */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <h2>Agent Console</h2>
          <button
            type="button"
            className={styles.newButton}
            onClick={handleNewConversation}
            disabled={creating}
          >
            + New analysis
          </button>
        </div>

        {/* History controls: mine vs everyone's, narrowed by starter + search. */}
        <div className={styles.historyControls}>
          <div className={styles.scopeTabs} role="tablist" aria-label="Conversation scope">
            <button
              type="button"
              role="tab"
              aria-selected={scope === 'mine'}
              className={`${styles.scopeTab} ${scope === 'mine' ? styles.scopeTabActive : ''}`}
              onClick={() => setScope('mine')}
            >
              Mine
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={scope === 'all'}
              className={`${styles.scopeTab} ${scope === 'all' ? styles.scopeTabActive : ''}`}
              onClick={() => setScope('all')}
            >
              All users
            </button>
          </div>
          {scope === 'all' && (
            <select
              className={styles.ownerSelect}
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              title="Filter by who started the conversation"
            >
              <option value="">Started by — anyone</option>
              {owners.map((o) => (
                <option key={o.user_id} value={o.user_id}>
                  {(o.name || o.email || 'Unknown') + ` (${o.conversation_count})`}
                </option>
              ))}
            </select>
          )}
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search titles…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className={styles.conversationList}>
          {conversations.length === 0 ? (
            <p className={styles.emptyState}>
              {search.trim() || (scope === 'all' && ownerFilter)
                ? 'No conversations match.'
                : 'No analyses yet.'}
            </p>
          ) : (
            conversations.map((convo) => {
              const badge = STATE_BADGE[convo.state];
              if (editingTitleId === convo.id) {
                return (
                  <div key={convo.id} className={styles.convoEditRow}>
                    <input
                      className={styles.convoEditInput}
                      value={titleDraft}
                      autoFocus
                      maxLength={200}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void commitTitleRename(convo.id, titleDraft);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditingTitleId(null);
                          setTitleDraft('');
                        }
                      }}
                      onBlur={() => {
                        setEditingTitleId(null);
                        setTitleDraft('');
                      }}
                      aria-label="Analysis name"
                    />
                    <button
                      type="button"
                      className={styles.convoEditSave}
                      // mousedown beats the input's blur, so commit isn't lost.
                      onMouseDown={(e) => {
                        e.preventDefault();
                        void commitTitleRename(convo.id, titleDraft);
                      }}
                      aria-label="Save name"
                    >
                      ✓
                    </button>
                  </div>
                );
              }
              return (
                <div key={convo.id} className={styles.convoRow}>
                  <button
                    type="button"
                    className={`${styles.conversationItem} ${
                      convo.id === activeId ? styles.activeConvo : ''
                    }`}
                    onClick={() => setActiveId(convo.id)}
                  >
                    <span className={styles.convoTitle}>
                      {convo.title || 'Untitled analysis'}
                    </span>
                    <span className={styles.convoOwner}>
                      <OwnerAvatar convo={convo} />
                      <span className={styles.convoOwnerName}>{ownerLabel(convo)}</span>
                    </span>
                    <span className={styles.convoMeta}>
                      <StatusBadge variant={badge.variant}>{badge.label}</StatusBadge>
                      <span className={styles.convoDate}>
                        {relativeDate(convo.last_activity_at)}
                      </span>
                    </span>
                  </button>
                  {scope === 'mine' && (
                    <button
                      type="button"
                      className={styles.convoRenameBtn}
                      onClick={() => {
                        setEditingTitleId(convo.id);
                        setTitleDraft(convo.title || '');
                      }}
                      aria-label="Rename analysis"
                      title="Rename"
                    >
                      ✎
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </aside>

      {/* Main transcript */}
      <main className={styles.main}>
        <div className={styles.readOnlyBanner}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {lockedProfileName ? (
            <span
              className={styles.bannerControl}
              title="This thread belongs to a locked door surface and always runs its own agent — the profile cannot be changed here. Use + New analysis to talk to a console agent."
            >
              <strong>Profile</strong>
              <span>🔒 {lockedProfileName}</span>
            </span>
          ) : profiles.length > 0 ? (
            <label
              className={styles.bannerControl}
              title="The agent configuration this conversation's next turn runs under"
            >
              <strong>Profile</strong>
              <select
                value={selectedProfileId}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedProfileId(id);
                  // Switching agent preselects that agent's configured default
                  // model (still overridable in the model picker beside it).
                  const prof = profiles.find((p) => p.id === id);
                  if (prof?.default_model) setSelectedModelId(prof.default_model);
                }}
                disabled={streaming}
                style={{ padding: '4px 8px', borderRadius: 6 }}
              >
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.writes ? ' — reads + records signals' : ' — read-only'}
                  </option>
                ))}
              </select>
            </label>
          ) : profilesLoad.error ? (
            <BannerLoadRetry
              label="Profile"
              subject="agent profiles"
              error={profilesLoad.error}
              loading={profilesLoad.loading}
              onRetry={profilesLoad.retry}
            />
          ) : (
            <span>
              <strong>Read-only data analyst.</strong> Queries Postgres, Sage
              (MySQL), and the Knowledge Bank.
            </span>
          )}
          {models.length > 0 ? (
            <label
              className={styles.bannerControl}
              title="The Anthropic model this conversation's next turn runs on"
            >
              <strong>Model</strong>
              <ModelSelect
                models={models}
                value={selectedModelId || globalDefaultModel}
                onChange={setSelectedModelId}
                disabled={streaming}
              />
            </label>
          ) : modelsLoad.error ? (
            <BannerLoadRetry
              label="Model"
              subject="models"
              error={modelsLoad.error}
              loading={modelsLoad.loading}
              onRetry={modelsLoad.retry}
            />
          ) : null}
          <div className={styles.bannerBody}>
            <span className={styles.bannerDescription}>
              {lockedProfileName && profileRetired
                ? `${lockedProfileName} is no longer an agent, so this conversation cannot take another turn — its history stays readable. Start a new analysis to continue the work.`
                : lockedProfileName
                ? `This conversation always runs ${lockedProfileName} — the agent of the door it was opened from.`
                : selectedProfile
                  ? selectedProfile.description
                  : 'Queries Postgres, Sage (MySQL), and the Knowledge Bank. It can never modify data.'}
            </span>
            <div className={styles.bannerActions}>
              {!lockedProfileName && selectedProfile && (selectedProfile.access_groups?.length ?? 0) > 0 && (
                <button
                  type="button"
                  className={styles.dataAccessButton}
                  onClick={() => setAccessModalOpen(true)}
                  title="What data this agent can access, in plain language"
                >
                  Can access
                </button>
              )}
              {!lockedProfileName && selectedProfileId && (
                <button
                  type="button"
                  className={styles.dataAccessButton}
                  onClick={() => setInspectorOpen(true)}
                  title="Inspect everything injected into this agent — prompt, tools, and the table-level data access"
                >
                  Inspect
                </button>
              )}
              <button
                type="button"
                className={styles.dataAccessButton}
                onClick={() => setSettingsOpen(true)}
                title="Edit the console's runtime settings (timeouts, concurrency)"
              >
                ⚙ Settings
              </button>
              {activeConversation && (
                <button
                  type="button"
                  className={styles.dataAccessButton}
                  onClick={handleShare}
                  title="Copy a shareable link to this conversation"
                >
                  {copiedHint ? '✓ Link copied' : '🔗 Share'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Errors render here so they're visible on the welcome screen too, not
            just inside an open conversation. */}
        {error && (
          <div className={styles.errorBar}>
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
              &times;
            </button>
          </div>
        )}

        {!activeConversation ? (
          <div className={styles.welcome}>
            <h3>Ask a question about your data</h3>
            <p>
              Start a new analysis to query orders, customers, products, and the
              Knowledge Bank in plain English.
            </p>
            <button
              type="button"
              className={styles.newButtonLarge}
              onClick={handleNewConversation}
              disabled={creating}
            >
              + New analysis
            </button>
          </div>
        ) : (
          <>
            {detail &&
              detail.conversation.id === activeId &&
              !viewerIsOwner && (
                <div className={styles.sharedBanner}>
                  <OwnerAvatar convo={detail.conversation} />
                  <span>
                    Shared view — started by{' '}
                    <strong>{ownerLabel(detail.conversation)}</strong>. Read-only;
                    only the starter can continue this analysis.
                  </span>
                </div>
              )}
            <div className={styles.transcript} ref={transcriptRef}>
              <div className={styles.transcriptInner}>
                {showEmptyTranscript && (
                  <div className={styles.emptyChat}>
                    <p>Ask the analyst a question to begin.</p>
                  </div>
                )}

                {timeline.map((item) =>
                  item.kind === 'user' ? (
                    <div
                      key={`msg-${item.message.id}`}
                      className={`${styles.bubble} ${styles.userBubble}`}
                    >
                      <p className={styles.userText}>{item.message.content}</p>
                      <MessageAttachmentChips
                        attachments={attachmentsByMsg.get(item.message.id)}
                      />
                      <div className={styles.meta}>
                        <MessageTimestamp at={item.message.created_at} />
                      </div>
                    </div>
                  ) : (
                    <div
                      key={`msg-${item.message.id}`}
                      className={`${styles.bubble} ${styles.assistantBubble}`}
                    >
                      <AssistantTurn
                        blocks={item.blocks}
                        isStreaming={false}
                        feedback={
                          detail &&
                          detail.conversation.id === activeId &&
                          detail.viewer_is_owner
                            ? {
                                basePath: '/admin/agent-console',
                                conversationId: detail.conversation.id,
                                messageId: item.message.id,
                                modelUsed: item.message.model ?? null,
                                strongestModelId: strongestModel || null,
                                strongestModelLabel:
                                  models.find((m) => m.id === strongestModel)
                                    ?.label ?? null,
                              }
                            : null
                        }
                      />
                      {/* What a headless conversation agent sent over its
                          channel this turn, and any decision it recorded —
                          the transcript reads as the exchange it was. */}
                      {item.sentMessages?.map((sent) => (
                        <SentMessageCard key={sent.key} message={sent} />
                      ))}
                      {item.decisions?.map((decision) => (
                        <ConversationDecisionCard key={decision.key} decision={decision} />
                      ))}
                      {/* Files the agent attached this turn (attach_file) —
                          one downloadable card each (images preview inline). */}
                      {item.agentFiles?.map((file) => (
                        <AgentFileCard
                          key={file.attachmentId}
                          base="/admin/agent-console"
                          file={file}
                          conversationId={activeId}
                        />
                      ))}
                      <div className={styles.meta}>
                        {item.message.input_tokens != null ||
                        item.message.output_tokens != null ||
                        item.message.cost_usd != null ? (
                          <>
                            {formatTokens(item.message) ?? '0 tokens'}
                            {item.message.cost_usd != null
                              ? ` · ${formatCostUsd(item.message.cost_usd)}`
                              : ''}
                            {item.message.model ? ` · ${item.message.model}` : ''}
                            {' · '}
                          </>
                        ) : null}
                        <MessageTimestamp at={item.message.created_at} />
                      </div>
                    </div>
                  ),
                )}

                {/* Live in-flight turn — same shape as the persisted turn so
                    the wire-vs-audit transition is visually seamless. Only shown
                    on the thread the turn actually runs in (``liveIsActive``);
                    the live scratch state is page-global, so without this gate it
                    would bleed onto a conversation you switch to mid-turn. */}
                {liveIsActive && liveUserPrompt && (
                  <div className={`${styles.bubble} ${styles.userBubble}`}>
                    <p className={styles.userText}>{liveUserPrompt}</p>
                    <MessageAttachmentChips
                      attachments={liveUserAttachments ?? undefined}
                    />
                  </div>
                )}
                {liveIsActive && (streaming || liveBlocks.length > 0) && (
                  <div className={`${styles.bubble} ${styles.assistantBubble}`}>
                    {liveBlocks.length > 0 ? (
                      <AssistantTurn blocks={liveBlocks} isStreaming={streaming} />
                    ) : (
                      <span className={styles.thinkingDots}>Thinking…</span>
                    )}
                    {liveAgentFiles.map((file) => (
                      <AgentFileCard
                        key={file.attachmentId}
                        base="/admin/agent-console"
                        file={file}
                        conversationId={activeId}
                      />
                    ))}
                    {liveUsage && <UsageMeta usage={liveUsage} />}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.inputArea} {...attach.dropZoneProps}>
              <DropVeil active={attach.isDragging} />
              <AttachmentTray
                files={attach.files}
                attachError={attach.attachError}
                onAddFiles={attach.addFiles}
                onRemoveFile={attach.removeFile}
                disabled={!canSend}
              />
              <div className={styles.inputWrapper}>
                <textarea
                  ref={inputRef}
                  className={styles.input}
                  placeholder={composerPlaceholder({
                    streamingHere,
                    streamingElsewhere: streaming && !liveIsActive,
                    canSend,
                    viewerIsOwner,
                    state: effectiveState,
                    profileRetired,
                  })}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onPaste={attach.onPaste}
                  rows={1}
                  // Stay editable while THIS thread's turn streams so the user can
                  // steer it mid-run; only truly-closed (archived/shared) or a turn
                  // busy in another thread locks it.
                  disabled={!canSend && !streamingHere}
                />
                {streamingHere ? (
                  <>
                    {inputValue.trim() && (
                      <button
                        type="button"
                        className={styles.sendButton}
                        onClick={handleInject}
                        title="Send this to the running agent to steer it (it keeps its current work)"
                      >
                        Send
                      </button>
                    )}
                    <button
                      type="button"
                      className={styles.stopButton}
                      onClick={handleStop}
                      disabled={cancelling}
                      title="Stop the agent"
                    >
                      {cancelling ? 'Stopping…' : 'Stop'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={styles.sendButton}
                    onClick={handleSend}
                    disabled={
                      !canSend || (!inputValue.trim() && attach.files.length === 0)
                    }
                  >
                    Send
                  </button>
                )}
              </div>
              <div className={styles.inputMeta}>
                <span>{activeConversation.model}</span>
                {effectiveState && (
                  <StatusBadge variant={STATE_BADGE[effectiveState].variant}>
                    {STATE_BADGE[effectiveState].label}
                  </StatusBadge>
                )}
                <ConversationTotalsBadge
                  persisted={detail?.totals ?? EMPTY_TOTALS}
                  liveUsage={liveUsage}
                />
              </div>
            </div>
          </>
        )}
      </main>

      <ProfileAccessModal
        open={accessModalOpen}
        onClose={() => setAccessModalOpen(false)}
        profile={selectedProfile}
      />

      <ProfileInspectorPanel
        open={inspectorOpen}
        onClose={() => setInspectorOpen(false)}
        profileId={selectedProfileId}
      />

      <ConsoleSettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  );
}
